/**
 * Widget Registry for Home Page
 * 
 * Defines all available widgets with their configuration:
 * - id: Unique identifier used in layout persistence
 * - name: Display name for UI
 * - component: React component to render
 * - defaultRow: Default row position (1-4)
 * - defaultColumn: Default column in row (1-3)
 * - permissionRequired: Permission constant (optional)
 * - projectEligibility: Array of eligible project codes (empty = all)
 */

import { lazy } from 'react';
import { SHOW_MACHINE_EVENTS_PERMISSION, SHOW_CREDENTIALS_MANAGEMENT } from '../../utils/constants';

// Lazy load widget components for better performance
const ActiveDevicesMetric = lazy(() => import('./components/ActiveDevicesMetric'));
const PowerBIDashboard = lazy(() => import('./components/PowerBIDashboard'));
const FavouriteSerials = lazy(() => import('./components/FavouriteSerials'));
const FavouriteCredentials = lazy(() => import('./components/FavouriteCredentials'));
const SerialSearch = lazy(() => import('./components/SerialSearch'));

/**
 * Widget configuration type
 * @typedef {Object} WidgetConfig
 * @property {string} id - Unique widget identifier
 * @property {string} name - Display name
 * @property {React.ComponentType} component - React component
 * @property {number} defaultRow - Default row position (1-4)
 * @property {number} defaultColumn - Default column in row (1-3)
 * @property {string} [permissionRequired] - Permission constant
 * @property {string[]} [projectEligibility] - Eligible project codes
 * @property {string} [colSpan] - CSS col-span class
 * @property {string} [regionFeatureKey] - CHINA_UNAVAILABLE_FEATURES key gating this widget by data region
 */

export const WIDGET_IDS = {
  ACTIVE_DEVICES_METRIC: 'active-devices-metric',
  SERIAL_SEARCH: 'serial-search',
  POWER_BI_DASHBOARD: 'power-bi-dashboard',
  FAVOURITE_SERIALS: 'favourite-serials',
  FAVOURITE_CREDENTIALS: 'favourite-credentials',
};

/**
 * @type {WidgetConfig[]}
 */
export const WIDGET_REGISTRY = [
  {
    id: WIDGET_IDS.ACTIVE_DEVICES_METRIC,
    name: 'Device Analytics',
    component: ActiveDevicesMetric,
    defaultRow: 1,
    defaultColumn: 1,
    permissionRequired: SHOW_MACHINE_EVENTS_PERMISSION,
    colSpan: 'col-span-3',
    // Not available in the China data region: QuickSight dashboard has no China data.
    regionFeatureKey: 'deviceAnalytics',
  },
  {
    id: WIDGET_IDS.SERIAL_SEARCH,
    name: 'Device Serial Quick Search',
    component: SerialSearch,
    defaultRow: 2,
    defaultColumn: 1,
    permissionRequired: SHOW_MACHINE_EVENTS_PERMISSION,
    colSpan: 'col-span-1',
  },
  {
    id: WIDGET_IDS.FAVOURITE_SERIALS,
    name: 'My Saved Serials',
    component: FavouriteSerials,
    defaultRow: 2,
    defaultColumn: 2,
    permissionRequired: SHOW_MACHINE_EVENTS_PERMISSION,
    colSpan: 'col-span-2',
  },
  {
    id: WIDGET_IDS.POWER_BI_DASHBOARD,
    name: 'IoT PBI Dashboard',
    component: PowerBIDashboard,
    defaultRow: 3,
    defaultColumn: 1,
    // Eligibility is handled internally by the component based on project name
    // Shows for: NDG, Nespresso, Nescafe projects
    colSpan: 'col-span-1',
  },
  {
    id: WIDGET_IDS.FAVOURITE_CREDENTIALS,
    name: 'Favourite Credentials',
    component: FavouriteCredentials,
    defaultRow: 4,
    defaultColumn: 1,
    permissionRequired: SHOW_CREDENTIALS_MANAGEMENT,
    colSpan: 'col-span-3',
    // Not available in the China data region: getPhysicalUsers/credentials backend not configured there.
    regionFeatureKey: 'favouriteCredentials',
  },
];

/**
 * Get widget config by ID
 * @param {string} widgetId - Widget ID
 * @returns {WidgetConfig|undefined}
 */
export const getWidgetById = (widgetId) => {
  return WIDGET_REGISTRY.find((w) => w.id === widgetId);
};

/**
 * Get all widget IDs
 * @returns {string[]}
 */
export const getAllWidgetIds = () => {
  return WIDGET_REGISTRY.map((w) => w.id);
};

/**
 * Check if widget is eligible for a project
 * @param {WidgetConfig} widget - Widget config
 * @param {string} projectCode - Project code
 * @returns {boolean}
 */
export const isWidgetEligibleForProject = (widget, projectCode) => {
  if (!widget.projectEligibility || widget.projectEligibility.length === 0) {
    return true;
  }
  return widget.projectEligibility.includes(String(projectCode).toLowerCase());
};

export default WIDGET_REGISTRY;
